import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Percent, Calendar, DollarSign, Clock, Shield, FileText, Users, BookOpen, MessageCircle } from 'lucide-react';

const InvoiceFinance = () => {
  const fundingDetails = [
    {
      icon: <Percent className="w-12 h-12 text-accent-500" />,
      title: "Funding Amount",
      value: "85% – 98%",
      description: "Funding providers may advance a large percentage of the value of your unpaid invoices",
    },
    {
      icon: <Calendar className="w-12 h-12 text-accent-500" />,
      title: "Invoice Period",
      value: "90 – 120 Days",
      description: "Invoice finance typically applies to invoices that are due within a short payment period",
    },
    {
      icon: <DollarSign className="w-12 h-12 text-accent-500" />,
      title: "Typical Cost",
      value: "2% – 5%",
      description: "The cost of invoice finance is usually calculated as a percentage of the invoice value",
    },
  ];

  const features = [
    {
      icon: <Clock className="w-8 h-8 text-primary-600" />,
      title: "Minimum Trading History",
      description: "Businesses usually need to be trading for at least three months to apply for invoice finance",
    },
    {
      icon: <Users className="w-8 h-8 text-primary-600" />,
      title: "Customer Payment Record",
      description: "Invoice finance providers typically review your customers' payment history before approving funding",
    },
    {
      icon: <FileText className="w-8 h-8 text-primary-600" />,
      title: "Sales Ledger Required",
      description: "Businesses usually need to share their outstanding invoices or sales ledger to access invoice finance",
    },
    {
      icon: <Shield className="w-8 h-8 text-primary-600" />,
      title: "Personal Guarantees",
      description: "Some providers may require a director to provide a personal guarantee as part of the agreement",
    },
  ];

  const benefits = [
    "Pay suppliers on time",
    "Purchase inventory",
    "Cover operational costs",
    "Invest in business growth",
  ];

  const resources = [
    {
      icon: <BookOpen className="w-8 h-8 text-primary-600" />,
      title: "SME Funding Guide",
      description: "Learn about different funding options available for small and medium-sized businesses",
      button: "Read More",
    },
    {
      icon: <FileText className="w-8 h-8 text-primary-600" />,
      title: "Case Studies",
      description: "See how businesses have successfully secured funding through 1ST TRUST FINANCES",
      button: "View Case Studies",
    },
    {
      icon: <MessageCircle className="w-8 h-8 text-primary-600" />,
      title: "Speak to a Funding Specialist",
      description: "Our team can help you understand whether invoice finance is the right solution for your business",
      button: "Contact Us",
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="section bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-900 leading-tight">
                What is{' '}
                <span className="gradient-text">Invoice Finance</span>?
              </h1>
              <p className="text-xl text-gray-600 font-medium">
                Unlock cash from your unpaid invoices and improve your business cash flow.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Waiting for customers to pay invoices can create cash-flow challenges for many businesses. Invoice finance allows companies to access funding by using unpaid invoices as collateral.
              </p>
              <p className="text-gray-600 leading-relaxed">
                At 1ST TRUST FINANCES, we help businesses access invoice finance solutions that allow them to receive funding faster while continuing to grow their operations.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Invoice finance can provide fast access to working capital without waiting weeks or months for customers to pay their invoices.
              </p>
              <Link to="/apply-for-funding" className="btn-primary inline-flex items-center">
                Apply for Funding
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="invoiceGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#invoiceGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <rect x="50" y="50" width="200" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="100" height="20" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="120" width="140" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="150" width="120" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="180" width="160" height="50" rx="5" fill="#0056d6" />
                  <FileText className="absolute" x="180" y="60" width="50" height="50" fill="#0ea5e9" opacity="0.3" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is Invoice Finance */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="whatIsGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#whatIsGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <rect x="50" y="50" width="200" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="80" height="20" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="120" width="140" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="150" width="120" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="180" width="160" height="50" rx="5" fill="#0056d6" />
                  <DollarSign className="absolute" x="200" y="60" width="40" height="40" fill="#0ea5e9" opacity="0.3" />
                </g>
              </svg>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2 space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Invoice Finance Allows Early Payment of Your Invoices
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Invoice finance enables businesses to receive payment for their invoices before customers settle them.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Instead of waiting 30, 60, or even 90 days for payment, businesses can access a large portion of the invoice value almost immediately.
              </p>
              <p className="text-gray-600 leading-relaxed">
                This type of funding helps businesses maintain stable cash flow while continuing to operate and grow.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Funding Details Section */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Invoice Finance Funding Details
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Clear and transparent funding terms for your business.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {fundingDetails.map((detail, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-6">{detail.icon}</div>
                <h3 className="font-heading text-xl font-bold text-primary-900 mb-2">
                  {detail.title}
                </h3>
                <p className="text-2xl font-bold text-accent-600 mb-4">
                  {detail.value}
                </p>
                <p className="text-gray-600 leading-relaxed">
                  {detail.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Invoice Finance At a Glance */}
      <section className="section bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Invoice Finance at a Glance
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Key features and requirements of invoice finance.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-primary-50 to-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="font-heading text-lg font-bold text-primary-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How Invoice Finance Helps Businesses */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                How Invoice Finance Supports Business Growth
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Invoice finance helps businesses overcome cash-flow challenges caused by slow customer payments.
              </p>
              <p className="text-gray-600 leading-relaxed">
                By unlocking cash tied up in unpaid invoices, businesses can:
              </p>
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start text-gray-600">
                      <ArrowRight className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0 mt-0.5" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Instead of waiting for customers to pay, businesses can access funding quickly and keep operations running smoothly.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="helpsGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#helpsGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <rect x="50" y="50" width="200" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="80" height="20" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="120" width="140" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="150" width="120" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="180" width="160" height="50" rx="5" fill="#0056d6" />
                  <DollarSign className="absolute" x="180" y="60" width="50" height="50" fill="#0ea5e9" opacity="0.3" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Resources
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Helpful resources to guide your funding journey.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {resources.map((resource, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-primary-50 to-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-6">{resource.icon}</div>
                <h3 className="font-heading text-xl font-bold text-primary-900 mb-3">
                  {resource.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {resource.description}
                </p>
                <button className="btn-secondary w-full">
                  {resource.button}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section gradient-bg text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg viewBox="0 0 1000 1000" className="w-full h-full">
            <defs>
              <pattern id="grid3" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid3)" />
          </svg>
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Unlock Cash From Your Invoices Today
            </h2>
            <p className="text-xl text-gray-300 mb-4">
              1ST TRUST FINANCES connects businesses with funding providers offering flexible invoice finance solutions.
            </p>
            <p className="text-gray-300 mb-8">
              Receive funding against your unpaid invoices and keep your business moving forward.
            </p>
            <Link to="/apply-for-funding" className="inline-block bg-white text-primary-600 font-semibold px-8 py-4 rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1">
              Get Invoice Finance Quotes
              <ArrowRight className="inline ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default InvoiceFinance;
